import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { FiMessageCircle } from 'react-icons/fi';

import { useLocationDetails } from '~hooks/useLocationDetails';
import { UserContext } from '~contexts/user';

import Modal from '~components/modal';
import Button from '~components/button';
import Loader from '~components/loader';

import PaymentError from '../request/partials/error';

import './style.scss';

// Why the backend refused the chat, mapped to the screen we show for it. Anything
// unrecognised (including a failed check) becomes a retryable error rather than a
// payment prompt, so we never ask for money without knowing why.
const REASON_MODES = {
  no_subscription: 'first_time',
  limit_reached: 'limit_reached',
  subscription_expired: 'expired',
  wrong_location: 'wrong_location',
};

// Copy per state. Kept as a lookup rather than nested ternaries so adding a state
// stays a one-line change.
const COPY = {
  first_time: {
    title: ['first-time.title', 'Contact helpers in your area'],
    explanation: [
      'first-time.explanation',
      'A subscription lets you start conversations with helpers for this address. ' +
        'It includes 5 conversations per month.',
    ],
  },
  limit_reached: {
    title: ['limit-reached.title', 'You have used all your conversations this month'],
    explanation: [
      'limit-reached.explanation',
      'You have used all the conversations included for this address. You can wait for your monthly renewal, ' +
        'or continue to start another conversation with this helper now.',
    ],
  },
  expired: {
    title: ['expired.title', 'Your subscription has ended'],
    explanation: [
      'expired.explanation',
      'Your subscription for this address is no longer active. Renew it to keep contacting helpers.',
    ],
  },
  wrong_location: {
    title: ['wrong-location.title', 'This helper works at a different address'],
    explanation: [
      'wrong-location.explanation',
      'This helper works in a different area from your existing subscription. ' +
        'Continue to enter the postcode and address where you need help. You can then contact all available helpers for that address. Your existing subscriptions and profile address stay unchanged.',
    ],
  },
};

// Explains why the customer needs a subscription before the payment modal opens,
// so the two are never mixed on one screen. The price belongs to the payment
// modal that follows.
const ConnectsExplanation = ({ onContinue = () => {}, onClose = () => {} }) => {
  const intl = useIntl();
  const history = useHistory();

  const { websiteTitle } = useSelector((state) => state.domainConfig);
  const { locationId, reason } = useSelector((state) => state.payment);
  const { user, getSubscriptionPayments } = useContext(UserContext);
  const { locationDetails } = useLocationDetails(locationId, [locationId]);

  const [context, setContext] = useState({ mode: null });

  // The backend tells us why access is needed; the subscription payload only
  // supplies the numbers the copy needs (conversations used, renewal date).
  // The dialog is unmounted as soon as the customer continues to payment, so a
  // resolved request is ignored once that has happened or the inputs changed.
  useEffect(() => {
    let isCurrent = true;

    const resolveContext = async () => {
      const mode = REASON_MODES[reason] || 'error';
      if (mode === 'error' || user?.accountType !== 'client' || !user?.personId) {
        setContext({ mode });
        return;
      }
      try {
        const payments = await getSubscriptionPayments(user.personId);
        if (!isCurrent) {
          return;
        }
        const matchSlug = locationDetails?.slug;
        const payment = (payments || []).find(
          (p) => (locationId && p.placeId === locationId) || (matchSlug && p.location?.slug === matchSlug)
        );
        const ongoing = payment?.subscriptions?.find((s) => s.subscriptionStatus === 'ONGOING');
        const renewalTimestamp = payment?.nextRecurringDate || ongoing?.currentPeriodEnd;

        setContext({
          mode,
          used: ongoing ? ongoing.totalConnects - ongoing.remainingConnects : undefined,
          total: ongoing?.totalConnects,
          renewalDate: renewalTimestamp ? new Date(renewalTimestamp * 1000) : undefined,
        });
      } catch (err) {
        // The reason still stands even if the usage numbers are unavailable, so
        // keep the resolved mode and fall back to copy without them.
        if (isCurrent) {
          setContext({ mode });
        }
      }
    };

    resolveContext();

    return () => {
      isCurrent = false;
    };
  }, [reason, user?.personId, locationId, locationDetails?.slug]);

  const handleViewSubscription = () => {
    onClose();
    history.push('/account');
  };

  const format = ([id, defaultMessage]) =>
    intl.formatMessage({ id: `containers.payment.explanation.${id}`, defaultMessage });

  const { mode, used, total, renewalDate } = context;

  if (!mode) {
    return (
      <Modal.Wrapper isOpened onClose={onClose} className="c-payment-explanation">
        <Modal.Content>
          <Loader />
        </Modal.Content>
      </Modal.Wrapper>
    );
  }

  if (mode === 'error') {
    return (
      <Modal.Wrapper isOpened onClose={onClose} className="c-payment-explanation">
        <PaymentError onCancel={onClose}>
          {intl.formatMessage({
            id: 'containers.payment.explanation.error',
            defaultMessage: 'We could not check your subscription right now. Please close this and try again.',
          })}
        </PaymentError>
      </Modal.Wrapper>
    );
  }

  const copy = COPY[mode] || COPY.first_time;
  const isDutch = (intl.locale || '').startsWith('nl');
  const isLimitReached = mode === 'limit_reached';
  const hasUsage = isLimitReached && typeof used === 'number' && typeof total === 'number';

  return (
    <Modal.Wrapper isOpened onClose={onClose} className="c-payment-explanation">
      <Modal.Header>
        <div className="l-center">
          <img alt={websiteTitle.toLowerCase()} src="/images/logo.svg" />
          <h2 className="l-headline--secondary">{isDutch && mode === 'wrong_location' ? 'Deze hulp werkt in een andere regio' : format(copy.title)}</h2>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="c-payment-explanation__body">
          <p className="c-explanation__text">{isDutch && mode === 'wrong_location' ? 'Deze hulp werkt in een andere regio dan je huidige abonnement. Vul eerst de postcode en het adres in waar je hulp nodig hebt. Je kunt daarna alle beschikbare hulpen voor dat adres benaderen. Je bestaande abonnementen en profieladres blijven ongewijzigd.' : format(copy.explanation)}</p>

          {hasUsage && (
            <p className="c-explanation__usage">
              {intl.formatMessage(
                {
                  id: 'containers.payment.explanation.used',
                  defaultMessage: 'Used: {used} of {total} conversations',
                },
                { used, total }
              )}
            </p>
          )}

          {isLimitReached && renewalDate && (
            <p className="c-explanation__renewal">
              {intl.formatMessage(
                {
                  id: 'containers.payment.explanation.renewal',
                  defaultMessage: 'New conversations become available on {date}',
                },
                { date: intl.formatDate(renewalDate, { day: 'numeric', month: 'long', year: 'numeric' }) }
              )}
            </p>
          )}

          <p className="c-explanation__clarification">
            {intl.formatMessage({
              id: 'containers.payment.explanation.clarification',
              defaultMessage:
                'Conversations you have already started stay accessible. You only pay again when you start a new ' +
                'conversation and none are left for this month.',
            })}
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer className="c-payment-explanation__footer">
        <div className="c-payment-explanation__button">
          <Button onClick={onContinue} className="c-btn c-payment-explanation__button">
            <FiMessageCircle />
            {intl.formatMessage({
              id: 'containers.payment.explanation.cta',
              defaultMessage: 'Continue',
            })}
          </Button>
        </div>
        <Button onClick={handleViewSubscription} className="c-btn c-btn--secondary c-explanation__secondary">
          {intl.formatMessage({
            id: 'containers.payment.explanation.secondary',
            defaultMessage: 'View my subscription',
          })}
        </Button>
      </Modal.Footer>
    </Modal.Wrapper>
  );
};

export default ConnectsExplanation;
